/**
 * AdminUsers — /admin/users. Table of OJB users with spend, admin
 * flag, activity status, and deactivate-with-transfer.
 *
 * Columns:
 *   - User: avatar (Entra photo via ojb_team) + display name + creds
 *     + email + last sign-in
 *   - Role: title + office (from ojb_team)
 *   - Status: Active/Deactivated chip + last-7d activity dot
 *   - Drafts
 *   - MTD spend
 *   - Today spend
 *   - Daily limit: inline number input + live progress bar showing
 *     today's usage vs. the (possibly-edited) limit
 *   - Admin checkbox
 *   - Actions: reset tour, deactivate/reactivate
 *
 * Controls:
 *   - Search (name / email / title / office)
 *   - Filter chips: Active only, Admins only, Over 50% of daily limit
 *   - Click column headers to sort (default: MTD spend desc)
 *
 * Deactivation (unchanged from the previous version):
 *   - If the user has drafts, the dialog asks for a transfer target
 *     and reassigns ownership before setting is_active=false.
 *   - Finalized meetings stay with the deactivated user.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import { AttendeeAvatar } from '../../components/editor/AttendeeAvatar.tsx';
import { Search } from 'lucide-react';

interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  isAdmin: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  rateLimitDailyUsd: string;
  tourSigninsSeen: number;
  mtdSpendUsd: number;
  todaySpendUsd: number;
  lastActivityAt: string | null;
  draftCount: number;
  title: string | null;
  office: string | null;
  headshotUrl: string | null;
}

/** Active rate-limit override row for a user — fetched from
 *  GET /admin/rate-limit-overrides and merged into UserRow as a
 *  small "+$X thru Y" chip below the baseline editor. The
 *  override takes precedence over the baseline at request time
 *  (services/rateLimit.ts), so the UI surfaces both so admins
 *  can tell what's actually in effect. */
interface RateLimitOverride {
  userId: string;
  dailyUsd: number;
  expiresAt: string | null;
  reason: string | null;
  baselineUsd: number;
  setByAdminId: string;
  createdAt: string;
}

type SortKey =
  | 'displayName'
  | 'role'
  | 'status'
  | 'drafts'
  | 'mtdSpend'
  | 'todaySpend'
  | 'dailyLimit'
  | 'admin';
type SortDir = 'asc' | 'desc';

export function AdminUsers() {
  const qc = useQueryClient();
  const [deactivateTarget, setDeactivateTarget] = useState<AdminUser | null>(
    null,
  );
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState(true);
  const [filterAdmins, setFilterAdmins] = useState(false);
  const [filterPressure, setFilterPressure] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('mtdSpend');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const list = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<{ users: AdminUser[] }>('/admin/users'),
    refetchInterval: 60_000,
  });

  // Active rate-limit overrides — small list (only users with
  // active grants), fetched separately so /admin/users response
  // shape stays simple. Merged into UserRow via byUserId map.
  const overridesQuery = useQuery({
    queryKey: ['admin', 'rate-limit-overrides'],
    queryFn: () =>
      api.get<{ overrides: RateLimitOverride[] }>(
        '/admin/rate-limit-overrides',
      ),
    refetchInterval: 60_000,
  });
  const overridesByUserId = useMemo(() => {
    const m = new Map<string, RateLimitOverride>();
    for (const o of overridesQuery.data?.overrides ?? []) {
      m.set(o.userId, o);
    }
    return m;
  }, [overridesQuery.data]);

  const grantOverride = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: { dailyUsd: number; expiresAt: string; reason: string };
    }) =>
      api.post<{ override: RateLimitOverride }>(
        `/admin/users/${id}/rate-limit-override`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'rate-limit-overrides'] });
    },
  });

  const revokeOverride = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: true }>(`/admin/users/${id}/rate-limit-override`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'rate-limit-overrides'] });
    },
  });

  const patch = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<{ isAdmin: boolean; rateLimitDailyUsd: number }>;
    }) => api.patch<{ user: AdminUser }>(`/admin/users/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const reactivate = useMutation({
    mutationFn: (id: string) =>
      api.post<{ user: AdminUser }>(`/admin/users/${id}/reactivate`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const resetTour = useMutation({
    mutationFn: (id: string) =>
      api.post<{ user: AdminUser }>(`/admin/users/${id}/reset-tour`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });

  const rows = list.data?.users ?? [];

  // ── Filter + search + sort ──────────────────────────────────────
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows.filter((u) => {
      if (filterActive && !u.isActive) return false;
      if (filterAdmins && !u.isAdmin) return false;
      if (filterPressure) {
        const limit = Number.parseFloat(u.rateLimitDailyUsd);
        if (!(limit > 0) || u.todaySpendUsd / limit < 0.5) return false;
      }
      if (q) {
        const hay = [
          u.displayName,
          u.email,
          u.title ?? '',
          u.office ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    out = [...out].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'displayName':
          return a.displayName.localeCompare(b.displayName) * dir;
        case 'role': {
          const aRole = `${a.title ?? ''} ${a.office ?? ''}`.trim();
          const bRole = `${b.title ?? ''} ${b.office ?? ''}`.trim();
          return aRole.localeCompare(bRole) * dir;
        }
        case 'status': {
          // Active first when asc; deactivated first when desc.
          const av = a.isActive ? 1 : 0;
          const bv = b.isActive ? 1 : 0;
          return (av - bv) * dir;
        }
        case 'drafts':
          return (a.draftCount - b.draftCount) * dir;
        case 'mtdSpend':
          return (a.mtdSpendUsd - b.mtdSpendUsd) * dir;
        case 'todaySpend':
          return (a.todaySpendUsd - b.todaySpendUsd) * dir;
        case 'dailyLimit':
          return (
            (Number.parseFloat(a.rateLimitDailyUsd) -
              Number.parseFloat(b.rateLimitDailyUsd)) *
            dir
          );
        case 'admin':
          return ((a.isAdmin ? 1 : 0) - (b.isAdmin ? 1 : 0)) * dir;
        default:
          return 0;
      }
    });
    return out;
  }, [rows, search, filterActive, filterAdmins, filterPressure, sortKey, sortDir]);

  function onSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Sensible default: numbers descend first, strings ascend first.
      const numeric = ['drafts', 'mtdSpend', 'todaySpend', 'dailyLimit', 'admin'];
      setSortDir(numeric.includes(key) ? 'desc' : 'asc');
    }
  }

  return (
    <div className="space-y-3.5">
      <p className="mb-1 text-[13px] leading-[1.55] text-charcoal/65">
        Spend is rolling Claude API cost. MTD resets at 00:00 on the 1st of
        each month. Per-user rate limits override the firm default when set.
        Title + office are pulled from the OJB Company Wide Entra group.
      </p>

      {/* Search pill — mockup .search-pill: rounded-[14px], cloud/60
          + charcoal/15, 12/18 padding with absolutely-positioned icon
          on the left. Pine focus ring + bone fill on focus. */}
      <div className="relative">
        <Search
          size={16}
          aria-hidden
          className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/45"
        />
        <input
          {...NO_AUTOFILL}
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, role, or office…"
          className="w-full rounded-[14px] border border-charcoal/15 bg-cloud/60 py-3 pl-11 pr-4 text-[13px] text-charcoal placeholder:text-charcoal/45 focus:border-pine focus:bg-bone focus:outline-none focus:ring-[3px] focus:ring-pine/15"
        />
      </div>

      {/* Filter chip row + meta count on the right */}
      <div className="flex flex-wrap items-center gap-1.5">
        <FilterChip
          active={filterActive}
          onClick={() => setFilterActive((v) => !v)}
        >
          Active only
        </FilterChip>
        <FilterChip
          active={filterAdmins}
          onClick={() => setFilterAdmins((v) => !v)}
        >
          Admins only
        </FilterChip>
        <FilterChip
          active={filterPressure}
          onClick={() => setFilterPressure((v) => !v)}
        >
          ≥ 50% of daily limit
        </FilterChip>
        <span className="ml-auto text-[11px] text-charcoal/55">
          {visible.length} of {rows.length} {rows.length === 1 ? 'user' : 'users'}
        </span>
      </div>

      {list.isLoading && (
        <div className="py-8 text-center text-[13px] italic text-charcoal/55">
          Loading users…
        </div>
      )}
      {list.error && (
        <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 p-4 text-[13px] text-terra-cotta">
          {String((list.error as Error).message)}
        </div>
      )}

      {visible.length > 0 && (
        // Mockup .admin-table: bone bg + charcoal/8 + rounded-[14px]
        // + overflow-hidden so the row hover bg stays inside the
        // border. cloud/60 thead with 10px tracked-uppercase headers.
        <div className="overflow-x-auto rounded-[14px] border border-charcoal/8 bg-bone shadow-[0_1px_2px_rgba(43,42,47,0.04)]">
          <table className="w-full text-[13px]">
            <thead className="border-b border-charcoal/10 bg-cloud/60 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-charcoal/65">
              <tr>
                <Th sortKey="displayName" current={sortKey} dir={sortDir} onSort={onSort}>
                  User
                </Th>
                <Th sortKey="role" current={sortKey} dir={sortDir} onSort={onSort}>
                  Role
                </Th>
                <Th sortKey="status" current={sortKey} dir={sortDir} onSort={onSort}>
                  Status
                </Th>
                <Th sortKey="drafts" current={sortKey} dir={sortDir} onSort={onSort} align="right">
                  Drafts
                </Th>
                <Th sortKey="mtdSpend" current={sortKey} dir={sortDir} onSort={onSort} align="right">
                  MTD spend
                </Th>
                <Th sortKey="todaySpend" current={sortKey} dir={sortDir} onSort={onSort} align="right">
                  Today
                </Th>
                <Th sortKey="dailyLimit" current={sortKey} dir={sortDir} onSort={onSort} align="right">
                  Daily limit ($)
                </Th>
                <Th sortKey="admin" current={sortKey} dir={sortDir} onSort={onSort}>
                  Admin
                </Th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => (
                <UserRow
                  key={u.id}
                  u={u}
                  override={overridesByUserId.get(u.id) ?? null}
                  onPatch={(body) => patch.mutate({ id: u.id, body })}
                  onGrantOverride={(body) =>
                    grantOverride.mutate({ id: u.id, body })
                  }
                  onRevokeOverride={() => revokeOverride.mutate(u.id)}
                  onReactivate={() => reactivate.mutate(u.id)}
                  onResetTour={() => resetTour.mutate(u.id)}
                  onDeactivate={() => setDeactivateTarget(u)}
                  patchPending={patch.isPending}
                  overridePending={
                    grantOverride.isPending || revokeOverride.isPending
                  }
                  reactivatePending={reactivate.isPending}
                  resetTourPending={resetTour.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {visible.length === 0 && !list.isLoading && !list.error && (
        <div className="rounded-2xl border border-charcoal/15 bg-cloud/60 py-10 text-center text-sm italic text-charcoal/50 shadow-sm">
          No users match your filters.
        </div>
      )}

      {deactivateTarget && (
        <DeactivateDialog
          target={deactivateTarget}
          candidates={rows.filter(
            (r) => r.isActive && r.id !== deactivateTarget.id,
          )}
          onClose={() => setDeactivateTarget(null)}
          onDone={() => {
            setDeactivateTarget(null);
            qc.invalidateQueries({ queryKey: ['admin', 'users'] });
          }}
        />
      )}
    </div>
  );
}

/* ─── Row ─────────────────────────────────────────────────────────── */

function UserRow({
  u,
  override,
  onPatch,
  onGrantOverride,
  onRevokeOverride,
  onReactivate,
  onResetTour,
  onDeactivate,
  patchPending,
  overridePending,
  reactivatePending,
  resetTourPending,
}: {
  u: AdminUser;
  override: RateLimitOverride | null;
  onPatch: (body: Partial<{ isAdmin: boolean; rateLimitDailyUsd: number }>) => void;
  onGrantOverride: (body: { dailyUsd: number; expiresAt: string; reason: string }) => void;
  onRevokeOverride: () => void;
  onReactivate: () => void;
  onResetTour: () => void;
  onDeactivate: () => void;
  patchPending: boolean;
  overridePending: boolean;
  reactivatePending: boolean;
  resetTourPending: boolean;
}) {
  const nameParts = splitName(u.displayName);
  const isActive7d = (() => {
    if (!u.lastActivityAt) return false;
    const ageMs = Date.now() - new Date(u.lastActivityAt).getTime();
    return ageMs < 7 * 24 * 60 * 60 * 1000;
  })();

  // Mockup .admin-table tbody tr — hover gets a faint pine wash.
  // Deactivated rows render at 60% opacity (mockup spec).
  return (
    <tr
      className={
        'border-b border-charcoal/[0.06] last:border-b-0 transition-colors hover:bg-pine/[0.04] ' +
        (u.isActive ? '' : 'opacity-60')
      }
    >
      {/* User */}
      <td className="px-3.5 py-3">
        <div className="flex items-start gap-2.5">
          <AttendeeAvatar
            name={u.displayName}
            headshotUrl={u.headshotUrl}
            size={32}
          />
          <div className="min-w-0">
            <div className="truncate font-medium text-charcoal">
              {nameParts.displayName}
              {nameParts.credentials && (
                <span className="ml-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-pine/70">
                  {nameParts.credentials}
                </span>
              )}
            </div>
            <div className="truncate text-[11px] text-charcoal/60">
              {u.email}
            </div>
            {u.lastLoginAt && (
              <div className="text-[10px] text-charcoal/40">
                Last sign-in{' '}
                {new Date(u.lastLoginAt).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Role */}
      <td className="px-3.5 py-3 text-[12px]">
        {u.title || u.office ? (
          <div>
            {u.title && <div className="text-charcoal">{u.title}</div>}
            {u.office && (
              <div className="text-[11px] text-charcoal/55">{u.office}</div>
            )}
          </div>
        ) : (
          <span className="text-charcoal/35">-</span>
        )}
      </td>

      {/* Status + 7d activity dot */}
      <td className="px-3.5 py-3 text-[12px]">
        <div className="flex items-center gap-2">
          <span
            className={
              'inline-block h-2 w-2 rounded-full ' +
              (isActive7d ? 'bg-pine' : 'bg-charcoal/25')
            }
            title={
              u.lastActivityAt
                ? `Last activity ${new Date(u.lastActivityAt).toLocaleString()}`
                : 'No activity recorded'
            }
          />
          {u.isActive ? (
            <span className="text-charcoal">Active</span>
          ) : (
            <span className="text-charcoal/55">Deactivated</span>
          )}
        </div>
      </td>

      {/* Drafts */}
      <td className="px-3 py-2.5 text-right tabular-nums">{u.draftCount}</td>

      {/* MTD spend */}
      <td className="px-3 py-2.5 text-right tabular-nums">
        ${u.mtdSpendUsd.toFixed(2)}
      </td>

      {/* Today spend */}
      <td className="px-3 py-2.5 text-right tabular-nums">
        ${u.todaySpendUsd.toFixed(2)}
      </td>

      {/* Daily limit — baseline editor + override chip below.
          Baseline is the user's permanent cap; override is a
          temporary grant that takes precedence at request time
          (see services/rateLimit.ts). UI surfaces both so the
          admin can tell what's actually in effect. */}
      <td className="px-3 py-2.5 text-right">
        <RateLimitEditor
          value={u.rateLimitDailyUsd}
          todaySpend={u.todaySpendUsd}
          onSave={(v) => onPatch({ rateLimitDailyUsd: v })}
          disabled={!u.isActive || patchPending}
        />
        <RateLimitOverrideControl
          override={override}
          baselineUsd={Number.parseFloat(u.rateLimitDailyUsd)}
          disabled={!u.isActive || overridePending}
          onGrant={onGrantOverride}
          onRevoke={onRevokeOverride}
        />
      </td>

      {/* Admin checkbox */}
      <td className="px-3 py-2.5">
        <label className="inline-flex items-center gap-1 text-[11px] text-charcoal">
          <input
            type="checkbox"
            checked={u.isAdmin}
            disabled={!u.isActive || patchPending}
            onChange={(e) => onPatch({ isAdmin: e.target.checked })}
            className="accent-pine"
          />
          Admin
        </label>
      </td>

      {/* Actions */}
      <td className="px-3 py-2.5 text-right text-[11px]">
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => {
              if (
                confirm(
                  `Reset the onboarding tour for ${u.displayName}? They'll see the tour again on their next three sign-ins.`,
                )
              ) {
                onResetTour();
              }
            }}
            disabled={resetTourPending}
            title={
              u.tourSigninsSeen >= 3
                ? 'Tour has retired for this user - reset to replay on next sign-ins.'
                : `Tour has fired on ${u.tourSigninsSeen} of 3 sign-ins.`
            }
            // Mockup .text-action: 11px charcoal/50, hover pine
            // (or terra-cotta for the --danger variant).
            className="text-[11px] text-charcoal/50 transition-colors hover:text-pine disabled:opacity-50"
          >
            Reset tour
            <span className="ml-1 text-[10px] text-charcoal/35">
              ({u.tourSigninsSeen}/3)
            </span>
          </button>
          {u.isActive ? (
            // .text-action--danger
            <button
              type="button"
              onClick={onDeactivate}
              className="text-[11px] text-charcoal/50 transition-colors hover:text-terra-cotta"
            >
              Deactivate
            </button>
          ) : (
            <button
              type="button"
              onClick={onReactivate}
              disabled={reactivatePending}
              className="text-[11px] text-charcoal/50 transition-colors hover:text-pine disabled:opacity-50"
            >
              Reactivate
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ─── Rate-limit inline editor with live spend bar ─────────────── */

function RateLimitEditor({
  value,
  todaySpend,
  onSave,
  disabled,
}: {
  value: string;
  todaySpend: number;
  onSave: (v: number) => void;
  disabled: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const parsedDraft = Number.parseFloat(draft);
  const effectiveLimit = Number.isFinite(parsedDraft) && parsedDraft > 0
    ? parsedDraft
    : Number.parseFloat(value) || 0;
  const pct = effectiveLimit > 0 ? Math.min(1, todaySpend / effectiveLimit) : 0;
  // Mockup .rate-editor: cream input + charcoal/20 + 12px tabular,
  // h-1 charcoal/10 bar with pine fill (terra-cotta over cap, amber
  // 95-99%). Width 96px container.
  const barColor =
    pct >= 1 ? 'bg-terra-cotta' : pct >= 0.95 ? 'bg-amber-500' : 'bg-pine';

  return (
    <div className="inline-flex w-24 flex-col items-stretch gap-1">
      <input
        {...NO_AUTOFILL}
        type="number"
        min={0}
        step={0.5}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const parsed = Number.parseFloat(draft);
          if (
            Number.isFinite(parsed) &&
            parsed !== Number.parseFloat(value)
          ) {
            onSave(parsed);
          }
        }}
        disabled={disabled}
        className="w-full rounded-[8px] border border-charcoal/20 bg-cream px-2 py-1 text-right text-[12px] tabular-nums text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15 disabled:opacity-50"
      />
      <div
        className="h-1 w-full overflow-hidden rounded-full bg-charcoal/10"
        title={`$${todaySpend.toFixed(2)} spent today (${Math.round(pct * 100)}% of limit)`}
      >
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
    </div>
  );
}

/* ─── Filter chip ─────────────────────────────────────────────────── */

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  // Mockup .filter-chip: cloud/60 bg + charcoal/20 border + 12px
  // medium charcoal, hover pine border + pine text. Active variant
  // (.filter-chip--active): pine fill + cream text. Padding 5/12px.
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ' +
        (active
          ? 'border-pine bg-pine text-cream'
          : 'border-charcoal/20 bg-cloud/60 text-charcoal hover:border-pine hover:text-pine')
      }
    >
      {children}
    </button>
  );
}

/* ─── Sortable header cell ────────────────────────────────────────── */

function Th({
  sortKey: key,
  current,
  dir,
  onSort,
  align = 'left',
  children,
}: {
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  // Mockup .admin-table thead th: 10px charcoal/65 bold uppercase
  // tracking-[0.14em] padding 10/14. Active sort col flips to pine.
  const isActive = current === key;
  return (
    <th
      className={
        'cursor-pointer select-none px-3.5 py-2.5 transition-colors hover:text-pine ' +
        (align === 'right' ? 'text-right ' : '') +
        (isActive ? 'text-pine' : '')
      }
      onClick={() => onSort(key)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {isActive && (
          <span className="text-[9px] leading-none">
            {dir === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </span>
    </th>
  );
}

/* ─── Rate-limit override control ─────────────────────────────── */

/**
 * Renders below the baseline RateLimitEditor in the Daily-limit
 * column. Two visual states:
 *
 *   - Active override: a small pine chip "+$X thru Mar 15"
 *     with click-to-revoke. Hover shows the reason + admin who
 *     granted it.
 *   - No override: a tiny "+ Override" link that opens the
 *     grant popover (dailyUsd / expiresAt / reason).
 */
function RateLimitOverrideControl({
  override,
  baselineUsd,
  disabled,
  onGrant,
  onRevoke,
}: {
  override: RateLimitOverride | null;
  baselineUsd: number;
  disabled: boolean;
  onGrant: (body: { dailyUsd: number; expiresAt: string; reason: string }) => void;
  onRevoke: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftUsd, setDraftUsd] = useState<string>(
    String(Math.max(baselineUsd * 2, baselineUsd + 10)),
  );
  // Default expiry: end of today, local time. Admins almost
  // always want "today only" as the temporary grant; longer
  // grants get explicitly typed.
  const defaultExpiry = useMemo(() => {
    const d = new Date();
    d.setHours(23, 59, 0, 0);
    // datetime-local format: YYYY-MM-DDTHH:MM
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  }, []);
  const [draftExpiry, setDraftExpiry] = useState(defaultExpiry);
  const [draftReason, setDraftReason] = useState('');

  if (override) {
    const expiresLabel = override.expiresAt
      ? new Date(override.expiresAt).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      : 'no expiry';
    return (
      <div className="mt-1 flex items-center justify-end gap-1 text-[10px]">
        {/* Mockup .override-chip: pine-soft + pine/30 + pine,
            rounded-full padding 2/8 + 10px medium with revoke ✕. */}
        <span
          className="inline-flex items-center gap-1 rounded-full border border-pine/30 bg-pine/[0.08] px-2 py-0.5 font-medium text-pine"
          title={`Override: $${override.dailyUsd.toFixed(2)} until ${expiresLabel}\nReason: ${override.reason ?? '(none given)'}`}
        >
          +${override.dailyUsd.toFixed(0)} thru {expiresLabel}
          <button
            type="button"
            onClick={onRevoke}
            disabled={disabled}
            className="text-pine/60 transition-colors hover:text-terra-cotta disabled:cursor-not-allowed disabled:opacity-50"
            title="Revoke this override"
          >
            ✕
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="mt-1 flex items-center justify-end text-[11px]">
      {!open ? (
        // Mockup .override-add: 11px charcoal/55, hover pine.
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          className="text-charcoal/55 transition-colors hover:text-pine disabled:cursor-not-allowed disabled:opacity-50"
        >
          + Temporary override
        </button>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const dollars = Number.parseFloat(draftUsd);
            if (!Number.isFinite(dollars) || dollars <= 0) return;
            if (!draftReason.trim()) return;
            // datetime-local has no timezone — toISOString
            // converts via the browser's local TZ which is what
            // the admin intuitively meant ("today through 6 PM
            // MY time").
            const expiresAt = new Date(draftExpiry).toISOString();
            onGrant({
              dailyUsd: dollars,
              expiresAt,
              reason: draftReason.trim(),
            });
            setOpen(false);
            setDraftReason('');
          }}
          className="flex flex-col items-stretch gap-1.5 rounded-[10px] border border-charcoal/12 bg-bone p-2.5 text-left text-[11px] shadow-[0_4px_12px_rgba(43,42,47,0.08)]"
          style={{ minWidth: 220 }}
        >
          <label className="flex items-center gap-2">
            <span className="w-14 text-charcoal/70">New cap</span>
            <input
              type="number"
              min={1}
              max={1000}
              step={1}
              value={draftUsd}
              onChange={(e) => setDraftUsd(e.target.value)}
              className="w-20 rounded-[6px] border border-charcoal/15 bg-cream px-1.5 py-0.5 text-right tabular-nums focus:border-pine focus:outline-none focus:ring-[2px] focus:ring-pine/15"
            />
            <span className="text-charcoal/55">USD</span>
          </label>
          <label className="flex items-center gap-2">
            <span className="w-14 text-charcoal/70">Until</span>
            <input
              type="datetime-local"
              value={draftExpiry}
              onChange={(e) => setDraftExpiry(e.target.value)}
              className="flex-1 rounded-[6px] border border-charcoal/15 bg-cream px-1.5 py-0.5 focus:border-pine focus:outline-none focus:ring-[2px] focus:ring-pine/15"
            />
          </label>
          <label className="flex items-start gap-2">
            <span className="w-14 pt-1 text-charcoal/70">Reason</span>
            <input
              {...NO_AUTOFILL}
              type="text"
              value={draftReason}
              onChange={(e) => setDraftReason(e.target.value)}
              placeholder="e.g. client deliverable today"
              className="flex-1 rounded-[6px] border border-charcoal/15 bg-cream px-1.5 py-0.5 focus:border-pine focus:outline-none focus:ring-[2px] focus:ring-pine/15"
              required
            />
          </label>
          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-charcoal/55 hover:text-charcoal"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!draftReason.trim() || disabled}
              className="rounded-full bg-pine px-3 py-1 text-[11px] font-medium text-cream transition-colors hover:bg-pine-deep disabled:opacity-50"
            >
              Grant
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ─── Name split helper ────────────────────────────────────────── */

/** "Sarah Chen, PLA, ASLA" → { displayName: "Sarah Chen", credentials: "PLA, ASLA" } */
function splitName(fullName: string): {
  displayName: string;
  credentials: string;
} {
  const commaIdx = fullName.indexOf(',');
  if (commaIdx < 0) return { displayName: fullName.trim(), credentials: '' };
  return {
    displayName: fullName.slice(0, commaIdx).trim(),
    credentials: fullName
      .slice(commaIdx + 1)
      .trim()
      .toUpperCase(),
  };
}

/* ─── Deactivate dialog ──────────────────────────────────────────── */

function DeactivateDialog({
  target,
  candidates,
  onClose,
  onDone,
}: {
  target: AdminUser;
  candidates: AdminUser[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [transferTo, setTransferTo] = useState<string>(candidates[0]?.id ?? '');

  const deactivate = useMutation({
    mutationFn: () =>
      api.post<{ user: AdminUser; transferredDrafts: number }>(
        `/admin/users/${target.id}/deactivate`,
        target.draftCount > 0 ? { transferDraftsToUserId: transferTo } : {},
      ),
    onSuccess: () => onDone(),
  });

  const errMsg =
    deactivate.error instanceof ApiError
      ? deactivate.error.message
      : deactivate.error instanceof Error
        ? deactivate.error.message
        : null;

  const canSubmit =
    target.draftCount === 0 ? true : !!transferTo && !deactivate.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-charcoal/12 bg-bone shadow-[0_18px_40px_rgba(43,42,47,0.18)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-charcoal/10 px-5 py-4">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-pine">
            Deactivate user
          </h2>
          <p className="mt-1 text-[12px] text-charcoal/65">
            Marks {target.displayName} ({target.email}) as inactive. They won't
            be able to sign in until reactivated.
          </p>
        </div>
        <div className="px-5 py-4 text-sm">
          {target.draftCount === 0 ? (
            <p className="text-charcoal">
              {target.displayName} has no open drafts. Finalized meetings stay
              assigned to them (firm-visible). Continue?
            </p>
          ) : (
            <>
              <p className="mb-3 text-charcoal">
                {target.displayName} has{' '}
                <b>
                  {target.draftCount} open draft
                  {target.draftCount === 1 ? '' : 's'}
                </b>
                . Transfer ownership to:
              </p>
              {candidates.length === 0 ? (
                <div className="rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                  No other active users to transfer to. Activate or create one
                  first.
                </div>
              ) : (
                <select
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                  className="w-full rounded-[10px] border border-charcoal/15 bg-cream px-3 py-2 text-[13px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
                >
                  {candidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName} - {c.email}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
          {errMsg && (
            <div className="mt-3 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-3 py-2 text-[12px] text-terra-cotta">
              {errMsg}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-charcoal/10 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Cancel
          </button>
          {/* Destructive - terra-cotta fill (deactivation is an
              irreversible-ish action; reactivate is a separate flow). */}
          <button
            type="button"
            onClick={() => deactivate.mutate()}
            disabled={!canSubmit}
            className="rounded-full bg-terra-cotta px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-terra-cotta/90 disabled:opacity-50"
          >
            {deactivate.isPending ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}
